import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { AuthTokens, ThemePreference, User } from "@/types/domain";

// ─── State shape ──────────────────────────────────────────────────
export interface UserState {
  currentUser: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  theme: ThemePreference;
  isLoading: boolean;
  error: string | null;
  attendeeStatus: string | null;
  isRestoringSession: boolean;
}

const initialState: UserState = {
  currentUser: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  theme: "system",
  isLoading: false,
  error: null,
  attendeeStatus: null,
  isRestoringSession: true,
};

// ─── Slice ────────────────────────────────────────────────────────
const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setCredentials(
      state,
      action: PayloadAction<{ user: User; tokens?: AuthTokens }>,
    ) {
      state.currentUser = action.payload.user;
      // state.accessToken = action.payload.tokens?.accessToken;
      // state.refreshToken = action.payload.tokens?.refreshToken;
      state.isAuthenticated = true;
      state.error = null;
      state.isLoading = false;
    },

    // Called when the access token is silently refreshed
    rotateTokens(state, action: PayloadAction<AuthTokens>) {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
    },

    updateProfile(state, action: PayloadAction<Partial<User>>) {
      if (state.currentUser) {
        state.currentUser = { ...state.currentUser, ...action.payload };
      }
    },

    setTheme(state, action: PayloadAction<ThemePreference>) {
      state.theme = action.payload;
    },

    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },

    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
      state.isLoading = false;
    },

    logout(state) {
      state.currentUser = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.error = null;
      state.isLoading = false;
      state.attendeeStatus = null;
      state.isRestoringSession = false;
    },
    setAttendeeStatus(state, action: PayloadAction<string | null>) {
      state.attendeeStatus = action.payload;
    },
    setRestoringSession(state, action: PayloadAction<boolean>) {
      state.isRestoringSession = action.payload;
    },
  },
});

export const {
  setCredentials,
  rotateTokens,
  updateProfile,
  setTheme,
  setLoading,
  setError,
  logout,
  setAttendeeStatus,
  setRestoringSession,
} = userSlice.actions;

export default userSlice.reducer;
